import React, { useState, useEffect } from 'react';
import { SideBar } from '../../layout';
import * as mui from '@mui/material';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import CustomSpeedDial from '../../common/SpeedDial';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Modal from '../../common/Modal';
import ResponsiveContainer from '../../layout/Container';
import EditIcon from '@mui/icons-material/Edit';
import { motion } from 'framer-motion';
import companyService from '../../../services/CompanyService';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { useNavigate } from 'react-router-dom';
import DynamicSnackbar from '../../common/Snackbar';
import NormalTextField from '../../common/NormalTextField';
import { ArrowRightCircleIcon } from '@heroicons/react/20/solid';
import AuthService from '../../../services/AuthService';


const Companies = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');  // Assuming token is stored in localStorage
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [isLoading, setIsLoading] = useState(true);
    const [displayMode, setDisplayMode] = useState('grid'); // 'grid' or 'list'
    const [searchQuery, setSearchQuery] = useState('');

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const [openCreateModal, setOpenCreateModal] = useState(false);

    const [companyData, setCompanyData] = useState({
        COMPANY_ID: '',
        COMPANY_NAME: ''
    });

    // Add these new state variables for field validation
    const [idError, setIdError] = useState(false);
    const [idErrorMessage, setIdErrorMessage] = useState('');
    const [nameError, setNameError] = useState(false);
    const [nameErrorMessage, setNameErrorMessage] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedData = { ...companyData, [name]: value };
        setCompanyData(updatedData);
        
        // Show validation errors in real-time
        if (name === 'COMPANY_ID') {
            if (!value.trim()) {
                setIdError(true);
                setIdErrorMessage('Company ID is required');
            } else {
                // Check for duplicate ID
                const duplicateId = companies.some(company => 
                    company.COMPANY_ID.toLowerCase() === value.toLowerCase()
                );
                
                if (duplicateId) {
                    setIdError(true);
                    setIdErrorMessage('This Company ID already exists');
                } else {
                    setIdError(false);
                    setIdErrorMessage('');
                }
            }
        } else if (name === 'COMPANY_NAME') {
            if (!value.trim()) {
                setNameError(true);
                setNameErrorMessage('Company Name is required');
            } else {
                // Check for duplicate Name
                const duplicateName = companies.some(company => 
                    company.COMPANY_NAME.toLowerCase() === value.toLowerCase()
                );
                
                if (duplicateName) {
                    setNameError(true);
                    setNameErrorMessage('This Company Name already exists');
                } else {
                    setNameError(false);
                    setNameErrorMessage('');
                }
            }
        }
        
        // Validate form after each change
        validateForm(updatedData);
    };

    // Function to validate form in real-time
    const validateForm = (data = companyData) => {
        // Check if both fields have values
        const hasValues = data.COMPANY_ID.trim() !== '' && data.COMPANY_NAME.trim() !== '';
        
        // Check for duplicates
        const hasDuplicateId = companies.some(company => 
            company.COMPANY_ID.toLowerCase() === data.COMPANY_ID.toLowerCase()
        );
        
        const hasDuplicateName = companies.some(company => 
            company.COMPANY_NAME.toLowerCase() === data.COMPANY_NAME.toLowerCase()
        );
        
        // Form is valid if it has values and no duplicates
        const valid = hasValues && !hasDuplicateId && !hasDuplicateName;
        setIsFormValid(valid);
        
        return valid;
    };

    // Function to validate company data on submission
    const validateCompanyData = () => {
        let isValid = true;
        
        // Check for empty fields
        if (!companyData.COMPANY_ID.trim()) {
            setIdError(true);
            setIdErrorMessage('Company ID is required');
            isValid = false;
        }
        
        if (!companyData.COMPANY_NAME.trim()) {
            setNameError(true);
            setNameErrorMessage('Company Name is required');
            isValid = false;
        }
        
        // Check for duplicates
        if (companyData.COMPANY_ID.trim()) {
            const duplicateId = companies.find(company => 
                company.COMPANY_ID.toLowerCase() === companyData.COMPANY_ID.toLowerCase()
            );
            
            if (duplicateId) {
                setIdError(true);
                setIdErrorMessage('This Company ID already exists');
                isValid = false;
            }
        }
        
        if (companyData.COMPANY_NAME.trim()) {
            const duplicateName = companies.find(company => 
                company.COMPANY_NAME.toLowerCase() === companyData.COMPANY_NAME.toLowerCase()
            );
            
            if (duplicateName) {
                setNameError(true);
                setNameErrorMessage('This Company Name already exists');
                isValid = false;
            }
        }
        
        setIsFormValid(isValid);
        return isValid;
    };

    const createCompany = async (e) => {
        e.preventDefault();
        
        // Validate before submission
        if (!validateCompanyData()) {
            return;
        }
        
        try {
            const response = await companyService.createCompany(companyData);
            setCompanies([...companies, response]); 
            setSnackbarMessage('Company successfully created');
            setSnackbarSeverity('success');
            setSnackbarOpen(true); 
            handleCloseCreateModal();
        } catch (error) {
            setSnackbarMessage('There was a problem creating the company');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const [companies, setCompanies] = useState([]); 
    const username = AuthService.getUsername(); // Get the current username

    const fetchCompanies = async () => {
        setIsLoading(true);
        console.log("Current authenticated username:", username);
        try {
            console.log("Starting fetchCompanies API call...");
            const response = await companyService.fetchCompanies(); 
            console.log("Companies API response:", response);
            setCompanies(response); 
          } catch (error) {
            console.error('Error fetching companies:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
            }
            setSnackbarMessage('Failed to load companies');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
          }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const handleOpenCreateModal = () => {
        const initialData = {
            COMPANY_ID: '',
            COMPANY_NAME: ''
        };
        
        setCompanyData(initialData);
        
        // Set initial validation state - show errors for empty fields
        setIdError(true);
        setIdErrorMessage('Company ID is required');
        setNameError(true);
        setNameErrorMessage('Company Name is required');
        setIsFormValid(false);
        setOpenCreateModal(true);
    };

    const handleCloseCreateModal = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
            setOpenCreateModal(false);
    };

    function classNames(...classes) {
        return classes.filter(Boolean).join(' ');
      }

    // Helper function to get initials from COMPANY_NAME
    function getInitials(name) {
        if (!name) return '';
        return name
        .split(' ')  // Split name into words
            .map(word => word[0]?.toUpperCase() || '')  // Get the first letter of each word
        .join('');  // Join the letters together
    }

    // Function to get a fixed color based on title (for consistency)
    function getColorForCompany(companyName) {
        const colors = [
          'bg-pink-600',
          'bg-purple-600',
          'bg-yellow-500',
          'bg-green-500',
          'bg-blue-500',
          'bg-red-500',
          'bg-teal-500',
          'bg-indigo-600',
          'bg-orange-500',
        ];
        // Use a hash function to get a consistent color for each company
        let hash = 0;
        if (!companyName) return colors[0];
        for (let i = 0; i < companyName.length; i++) {
            hash = companyName.charCodeAt(i) + ((hash << 5) - hash);
        }
        hash = Math.abs(hash);
        return colors[hash % colors.length];
    }

    // Animation variants for staggered entry
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.06
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 12
            }
        }
    };

    // Filter companies based on search query
    const filteredCompanies = companies.filter(company => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            (company.COMPANY_ID && company.COMPANY_ID.toLowerCase().includes(query)) ||
            (company.COMPANY_NAME && company.COMPANY_NAME.toLowerCase().includes(query))
        );
    });

    const navigateToCompany = (companyId) => {
        navigate(`/companies/${companyId}`);
    };

    const customMainContent = (
        <div className="min-h-screen bg-gray-50">
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                            <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                            Home
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        System Settings
                    </mui.Link>
                    <mui.Typography color="text.primary">Companies</mui.Typography>
                </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <BusinessIcon sx={{ 
                            fontSize: 28, 
                            color: 'primary.main', 
                            mr: 2,
                            filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.1))'
                        }} />
                        <mui.Box sx={{ flexGrow: 1 }}>
                            <mui.Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Manage Companies
                            </mui.Typography>
                            <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Create, manage, and update company information
                </mui.Typography>
                        </mui.Box>
                        <mui.Box sx={{ display: 'flex', gap: 1.5 }}>
                            <mui.TextField
                                placeholder="Search..."
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <mui.InputAdornment position="start">
                                            <SearchIcon fontSize="small" color="action" />
                                        </mui.InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: '180px',
                                    backgroundColor: 'white',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1,
                                        height: 32
                                    }
                                }}
                            />
                            <mui.ButtonGroup
                                size="small"
                                sx={{
                                    height: 32,
                                    boxShadow: 'none'
                                }}
                            >
                                <mui.Button 
                                    variant={displayMode === 'grid' ? "contained" : "outlined"}
                                    onClick={() => setDisplayMode('grid')}
                                    startIcon={<mui.Icon fontSize="small">grid_view</mui.Icon>}
                                    sx={{
                                        textTransform: 'none',
                                        boxShadow: 'none'
                                    }}
                                >
                                    Grid
                                </mui.Button>
                                <mui.Button 
                                    variant={displayMode === 'list' ? "contained" : "outlined"}
                                    onClick={() => setDisplayMode('list')}
                                    startIcon={<mui.Icon fontSize="small">view_list</mui.Icon>}
                                    sx={{
                                        textTransform: 'none',
                                        boxShadow: 'none'
                                    }}
                                >
                                    List
                                </mui.Button>
                            </mui.ButtonGroup>
                            <mui.Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenCreateModal}
                                sx={{ 
                                    textTransform: 'none',
                                    height: 32,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                    }
                                }}
                                size="small"
                            >
                                Add
                            </mui.Button>
                        </mui.Box>
                    </mui.Box>

                    <Separator />

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 2,
                        minHeight: '70vh',
                        mt: 3,
                        boxShadow: 'inset 0 1px 2px 0 rgb(0 0 0 / 0.05)',
                        background: 'linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,0.5) 100%)'
                    }}>
                        <mui.Paper 
                            elevation={0}
                            sx={{ 
                                p: 2,
                                height: '100%',
                                backgroundColor: 'transparent'
                            }}
                        >
                            {isLoading ? (
                                <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                    <mui.CircularProgress size={30} />
                                </mui.Box>
                            ) : filteredCompanies.length === 0 ? (
                                <mui.Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    py: 6,
                                    textAlign: 'center'
                                }}>
                                    <BusinessIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
                                    <mui.Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                        No companies found
                                    </mui.Typography>
                                    <mui.Typography variant="body2" color="text.secondary" sx={{ maxWidth: 350, fontSize: '0.875rem' }}>
                                        {searchQuery ? 
                                            "No companies match your search criteria. Try a different search term." : 
                                            "You haven't added any companies yet. Get started by adding your first company."}
                                    </mui.Typography>
                                    {!searchQuery && (
                                        <mui.Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            onClick={handleOpenCreateModal}
                                            sx={{ mt: 2, textTransform: 'none' }}
                                            size="small"
                                        >
                                            Add Company
                                        </mui.Button>
                                    )}
                                </mui.Box>
                            ) : displayMode === 'grid' ? (
                                <motion.div 
                                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 mt-2"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    style={{ padding: '2px' }}
                                >
                                    {filteredCompanies.map((company) => (
                                        <motion.div 
                                            key={company.id || company.COMPANY_ID}
                                            variants={itemVariants}
                                            whileHover={{ scale: 1.01 }}
                                        >
                                            <mui.Paper
                                                elevation={0}
                                                sx={{
                                                    p: 1.5,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    backgroundColor: '#ffffff',
                                                    cursor: 'pointer',
                                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                                    borderRadius: 1,
                                                    transition: 'all 0.2s ease',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: '0',
                                                        background: 'linear-gradient(180deg, rgba(25, 118, 210, 0.04) 0%, rgba(255,255,255,0) 100%)',
                                                        transition: 'height 0.2s ease',
                                                        zIndex: 0
                                                    },
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                        transform: 'translateY(-1px)',
                                                        '&::before': {
                                                            height: '100%'
                                                        }
                                                    }
                                                }}
                                                onClick={() => navigateToCompany(company.id)}
                                            >
                                                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1, position: 'relative', zIndex: 1 }}>
                                                    <mui.Avatar
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            bgcolor: getColorForCompany(company.COMPANY_NAME),
                                                            fontSize: '0.8rem',
                                                            mr: 1.5,
                                                            fontWeight: 600,
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                            border: '2px solid white',
                                                            position: 'relative',
                                                            '&::after': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 50%)',
                                                                borderRadius: '50%'
                                                            }
                                                        }}
                                                    >
                                                        {getInitials(company.COMPANY_NAME)}
                                                    </mui.Avatar>
                                                    <mui.Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            fontSize: '0.65rem', 
                                                            color: 'primary.main', 
                                                            bgcolor: 'rgba(25, 118, 210, 0.05)',
                                                            border: '1px solid rgba(25, 118, 210, 0.1)',
                                                            borderRadius: 0.5,
                                                            px: 0.8,
                                                            py: 0.2,
                                                            ml: 'auto',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        {company.COMPANY_ID}
                                                    </mui.Typography>
                                                </mui.Box>
                                                
                                                <mui.Typography 
                                                    sx={{ 
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        color: 'text.primary',
                                                        lineHeight: 1.3,
                                                        position: 'relative',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    {company.COMPANY_NAME}
                                                </mui.Typography>
                                            </mui.Paper>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : (
                                <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="bg-white shadow-sm rounded-md overflow-hidden mt-2"
                                >
                                    <mui.Table size="small" sx={{ tableLayout: 'fixed' }}>
                                        <mui.TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                                            <mui.TableRow>
                                                <mui.TableCell width="40px"></mui.TableCell>
                                                <mui.TableCell width="120px">ID</mui.TableCell>
                                                <mui.TableCell>Company Name</mui.TableCell>
                                                <mui.TableCell width="40px"></mui.TableCell>
                                            </mui.TableRow>
                                        </mui.TableHead>
                                        <mui.TableBody>
                                            {filteredCompanies.map((company) => (
                                                <motion.tr
                                                    key={company.id || company.COMPANY_ID}
                                                    variants={itemVariants}
                                                    component={mui.TableRow}
                                                    hover
                                                    onClick={() => navigateToCompany(company.id)}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        '&:hover': { 
                                                            backgroundColor: '#f9fafb',
                                                            '& .arrow-icon': {
                                                                color: 'primary.main',
                                                                transform: 'translateX(2px)'
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <mui.TableCell>
                                                        <mui.Avatar
                                                            sx={{
                                                                bgcolor: getColorForCompany(company.COMPANY_NAME),
                                                                width: 30,
                                                                height: 30,
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                color: 'white',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                            }}
                                                        >
                                                            {getInitials(company.COMPANY_NAME)}
                                                        </mui.Avatar>
                                                    </mui.TableCell>
                                                    <mui.TableCell>
                                                        <mui.Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                                            {company.COMPANY_ID}
                                                        </mui.Typography>
                                                    </mui.TableCell>
                                                    <mui.TableCell>
                                                        <mui.Typography variant="body2">
                                                            {company.COMPANY_NAME}
                                                        </mui.Typography>
                                                    </mui.TableCell>
                                                    <mui.TableCell>
                                                        <ArrowRightCircleIcon className="h-4 w-4 text-gray-400 arrow-icon" style={{ transition: 'all 0.2s' }} />
                                                    </mui.TableCell>
                                                </motion.tr>
                                            ))}
                                        </mui.TableBody>
                                    </mui.Table>
                                </motion.div>
                            )}
                        </mui.Paper>
                    </mui.Box>

                <Modal
                    open={openCreateModal}
                    onClose={handleCloseCreateModal}
                        header="Add New Company"
                    body={
                            <mui.Box sx={{ px: 2 }}>
                            <NormalTextField
                                label="Company ID"
                                name="COMPANY_ID"
                                value={companyData.COMPANY_ID}
                                onChange={handleChange}
                                placeholder="Enter company ID"
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                error={idError}
                                helperText={idErrorMessage}
                            />
                            <NormalTextField
                                label="Company Name"
                                name="COMPANY_NAME"
                                value={companyData.COMPANY_NAME}
                                onChange={handleChange}
                                placeholder="Enter company name"
                                fullWidth
                                size="small"
                                error={nameError}
                                helperText={nameErrorMessage}
                            />
                            </mui.Box>
                    }
                    footer={
                            <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2 }}>
                                <mui.Button 
                                    onClick={handleCloseCreateModal} 
                                    variant="outlined"
                                    size="small"
                                    sx={{ textTransform: 'none' }}
                                >
                                Cancel
                            </mui.Button>
                                <mui.Button 
                                    onClick={createCompany} 
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    disabled={!isFormValid}
                                    sx={{ 
                                        textTransform: 'none',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                            color: 'rgba(25, 118, 210, 0.3)'
                                        }
                                    }}
                                >
                                Create
                            </mui.Button>
                            </mui.Box>
                    }
                />

                <DynamicSnackbar
                    open={snackbarOpen}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={handleSnackbarClose}
                />
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
}

export default Companies;
